import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, User, Building2, Phone, Mail, 
  MapPin, ShieldCheck, DollarSign, Tag,
  Briefcase, Heart, AlertCircle, Plus,
  Shield, Users, Lock
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { HouseholdContact, ContactCategory, ContactStream, Role } from '@/types';

interface ContactCreatorProps {
  onAdd: (contact: Partial<HouseholdContact>, targetScope: 'personal' | 'household') => void;
  onClose: () => void;
  currentUserId: string;
  currentHouseholdId: string | null;
  userRole?: Role;
}

export const ContactCreator: React.FC<ContactCreatorProps> = ({ 
  onAdd, 
  onClose, 
  currentUserId, 
  currentHouseholdId,
  userRole = Role.MEMBER 
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState<Partial<HouseholdContact>>({
    name: '',
    companyName: '',
    category: 'professional',
    stream: 'maintenance',
    subCategory: '',
    phones: [{ label: 'Mobile', number: '', isPrimary: true }],
    emails: [{ label: 'Work', address: '', isPrimary: true }],
    tags: [],
    notes: ''
  });

  // 🛡️ Scope Selection State
  const [targetScope, setTargetScope] = useState<'personal' | 'household'>(
    currentHouseholdId ? 'household' : 'personal'
  );

  // Sync scope if household status changes
  useEffect(() => {
    if (!currentHouseholdId) setTargetScope('personal');
  }, [currentHouseholdId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    /**
     * 🛡️ PERMISSION CHECK
     * Shared contacts require ADMIN/OWNER status or being a verified household member.
     */
    await onAdd({ // Await the onAdd call
      ...formData,
      // Remove client-side ID generation. Firestore will handle this.
      ownerId: targetScope === 'personal' ? currentUserId : (currentHouseholdId || currentUserId),
      localChangeTimestamp: Date.now(),
      isDeleted: false
    }, targetScope);
    
    onClose();
  };

  const updatePhone = (index: number, val: string) => {
    const newPhones = [...(formData.phones || [])];
    newPhones[index] = { ...newPhones[index], number: val };
    setFormData({ ...formData, phones: newPhones });
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-950 p-8 w-full max-w-2xl shadow-2xl animate-in slide-in-from-right duration-300 overflow-hidden relative">
      
      {/* 🛡️ Privacy Badge Overlay */}
      <div className="absolute top-4 left-8 flex items-center gap-2 px-3 py-1 bg-slate-50 dark:bg-slate-900 rounded-full border border-slate-100 dark:border-slate-800 z-20">
        {targetScope === 'personal' ? (
          <Shield className="w-3 h-3 text-emerald-500" />
        ) : (
          <Users className="w-3 h-3 text-brand" />
        )}
        <span className="text-[8px] font-black uppercase tracking-tighter text-slate-500">
          Target: {targetScope === 'personal' ? 'Private Partition' : 'Shared Hub'}
        </span>
      </div>

      {/* --- HEADER --- */}
      <header className="flex justify-between items-start mt-6 mb-8">
        <div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
            {t('contacts.add_new', 'New Directory Intake')}
          </h2>
          <p className="text-2xs font-black text-brand uppercase tracking-[0.3em] mt-2">
            {t('contacts.registry_intake', 'Hybrid Scoping v2.0')}
          </p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
          <X className="w-6 h-6 text-slate-400" />
        </button>
      </header>

      <form onSubmit={handleSubmit} className="flex-1 space-y-8 overflow-y-auto no-scrollbar pb-12">
        
        {/* --- SECTION 0: SCOPE SELECTION (THE HYBRID SWITCH) --- */}
        <section className="space-y-3">
          <div className="flex justify-between items-center ml-2">
            <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest">Target Repository</h3>
            {!currentHouseholdId && (
              <div className="flex items-center gap-1.5">
                <Lock className="w-2.5 h-2.5 text-amber-500" />
                <span className="text-[8px] font-black uppercase text-amber-500 tracking-tighter">Shared Hub Offline</span>
              </div>
            )}
          </div>
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl gap-1">
             <button
                type="button"
                onClick={() => setTargetScope('personal')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${targetScope === 'personal' ? 'bg-white shadow-md text-emerald-600' : 'text-slate-400'}`}
              >
                <Shield className="w-3.5 h-3.5" /> Personal
              </button>
              <button
                type="button"
                disabled={!currentHouseholdId}
                onClick={() => setTargetScope('household')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${targetScope === 'household' ? 'bg-white shadow-md text-brand' : 'text-slate-400 opacity-30 cursor-not-allowed'}`}
              >
                <Users className="w-3.5 h-3.5" /> Household
              </button>
          </div>
        </section>

        {/* --- SECTION 1: IDENTITY & CATEGORY --- */}
        <section className="space-y-4">
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1.5 rounded-2xl gap-1">
            {[
              { id: 'professional', icon: Briefcase, label: 'Pro' },
              { id: 'personal', icon: Heart, label: 'Personal' },
              { id: 'emergency', icon: AlertCircle, label: 'SOS' }
            ].map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setFormData({ ...formData, category: cat.id as ContactCategory })}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${formData.category === cat.id ? 'bg-white shadow-md text-brand' : 'text-slate-400'}`}
              >
                <cat.icon className="w-3.5 h-3.5" /> {cat.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                required
                placeholder="Contact Name"
                className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 font-bold outline-none focus:border-brand transition-all"
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="relative">
              <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                placeholder="Company (Optional)"
                className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 font-bold outline-none focus:border-brand transition-all"
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              />
            </div>
          </div>
        </section>

        {/* --- SECTION 2: COMMUNICATION --- */}
        <section className="space-y-4">
          <h3 className="text-2xs font-black uppercase text-slate-400 tracking-widest ml-2">{t('contacts.comms', 'Communication')}</h3>
          <div className="space-y-3">
            {formData.phones?.map((phone, idx) => (
              <div key={idx} className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input 
                  placeholder="Phone Number"
                  value={phone.number}
                  onChange={(e) => updatePhone(idx, e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 font-bold outline-none"
                />
              </div>
            ))}
          </div>
        </section>

        {/* --- SECTION 3: DOMAIN MAPPING (ENGINE ALIGNMENT) --- */}
        <section className="p-6 bg-indigo-50 dark:bg-indigo-900/10 rounded-[2.5rem] border border-indigo-100 dark:border-indigo-800/50">
          <div className="flex items-center gap-3 mb-4">
            <Tag className="w-4 h-4 text-brand" />
            <h3 className="text-2xs font-black uppercase text-brand tracking-widest">{t('contacts.domain', 'Workstream Alignment')}</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[9px] font-bold text-indigo-400 uppercase ml-1">Workstream</label>
              <select 
                value={formData.stream}
                onChange={(e) => setFormData({ ...formData, stream: e.target.value as ContactStream })}
                className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border-none font-bold text-xs"
              >
                <option value="maintenance">Maintenance</option>
                <option value="childcare">Childcare</option>
                <option value="pet">Pet Care</option>
                <option value="vehicle">Vehicle</option>
                <option value="medical">Medical</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-bold text-indigo-400 uppercase ml-1">Trade/Role</label>
              <input 
                placeholder="e.g. Plumber"
                className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border-none font-bold text-xs"
                onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
              />
            </div>
          </div>
        </section>

        {/* --- SECTION 4: PROFESSIONAL LEDGER --- */}
        {formData.category === 'professional' && (
          <section className="grid grid-cols-2 gap-4">
            <div className="relative">
              <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                placeholder="License #"
                className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 font-bold text-xs"
                onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
              />
            </div>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="number"
                placeholder="Hourly Rate"
                className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 font-bold text-xs"
                onChange={(e) => setFormData({ ...formData, hourlyRate: parseFloat(e.target.value) })}
              />
            </div>
          </section>
        )}

      </form>

      <footer className="pt-6 border-t border-slate-100 dark:border-slate-800">
        <Button 
          onClick={handleSubmit}
          disabled={!formData.name}
          className="w-full h-16 rounded-[2rem] bg-brand hover:bg-brand-hover text-white font-black text-lg gap-3 shadow-xl shadow-brand/30 transition-all active:scale-95"
        >
          <Plus className="w-6 h-6" /> {targetScope === 'personal' ? 'Save to Personal Directory' : 'Save to Household Directory'}
        </Button>
      </footer>
    </div>
  );
};